import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { logger } from '../utils/logger';
import { useSubscription } from '../hooks/useSubscription';

const AuthContext = createContext(null);

export function AuthProvider({ children, firebaseUser, onLogout }) {
  const userName = firebaseUser?.displayName || 'User';
  const userEmail = firebaseUser?.email || 'user@example.com';
  const userInitials =
    userName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';

  const [currentUser, setCurrentUser] = useState({
    name: userName,
    email: userEmail,
    initials: userInitials,
  });

  // Team workspace state
  const [teamOwnerId, setTeamOwnerId] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);

  const handleLogout = () => {
    if (onLogout) onLogout();
  };

  // ── Team Membership Check ─────────────────────────────────
  useEffect(() => {
    if (!firebaseUser) return;

    const membershipQuery = query(
      collection(db, 'teamMemberships'),
      where('memberEmail', '==', firebaseUser.email),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(
      membershipQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const membership = snapshot.docs[0].data();
          setTeamOwnerId(membership.teamOwnerId);
          setTeamInfo({
            teamCode: membership.teamCode,
            teamName: membership.teamName || 'Team Workspace',
            ownerName: membership.ownerName,
            ownerEmail: membership.ownerEmail || null,
            ownerId: membership.teamOwnerId,
            joinedAt: membership.joinedAt,
            role: membership.role || 'Member',
          });
        } else {
          setTeamOwnerId(null);
          setTeamInfo(null);
        }
      },
      (error) => {
        logger.error('Error checking team membership:', error);
      }
    );

    return () => unsubscribe();
  }, [firebaseUser]);

  // ── User Profile ──────────────────────────────────────────
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    if (!firebaseUser) return;
    const fetchProfile = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (docSnap.exists()) setUserProfile(docSnap.data());
      } catch (error) {
        logger.error('Error loading user profile:', error);
      }
    };
    fetchProfile();
  }, [firebaseUser]);

  // ── Owner Branding (for team members) ─────────────────────
  const [ownerProfile, setOwnerProfile] = useState(null);

  useEffect(() => {
    if (!teamOwnerId || !firebaseUser || teamOwnerId === firebaseUser.uid) {
      setOwnerProfile(null);
      return;
    }
    const fetchOwnerBranding = async () => {
      try {
        const tcQuery = query(collection(db, 'teamCodes'), where('ownerId', '==', teamOwnerId));
        const tcSnap = await getDocs(tcQuery);
        if (!tcSnap.empty) {
          const tcData = tcSnap.docs[0].data();
          setOwnerProfile({
            companyName: tcData.companyName || '',
            companyLogo: tcData.companyLogo || '',
            name: tcData.ownerName || '',
          });
        }
      } catch (error) {
        logger.error('Error loading owner branding:', error);
      }
    };
    fetchOwnerBranding();
  }, [teamOwnerId, firebaseUser]);

  const updateUserProfile = async (profileData) => {
    try {
      await setDoc(
        doc(db, 'users', firebaseUser.uid),
        {
          ...profileData,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setUserProfile((prev) => ({ ...prev, ...profileData }));
      return { success: true };
    } catch (error) {
      logger.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  };

  // ── Team Members & Current User Role ──────────────────────
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  useEffect(() => {
    if (!firebaseUser) {
      setCurrentUserRole(null);
      return;
    }
    if (!teamOwnerId || teamOwnerId === firebaseUser.uid) {
      setCurrentUserRole('Owner');
    } else if (teamInfo?.role) {
      setCurrentUserRole(teamInfo.role);
    } else {
      setCurrentUserRole('Member');
    }
  }, [firebaseUser, teamOwnerId, teamInfo]);

  // ── Department Teams ──────────────────────────────────────
  const [departmentTeams, setDepartmentTeams] = useState([]);
  const [departmentTeamsLoading, setDepartmentTeamsLoading] = useState(true);

  useEffect(() => {
    if (!firebaseUser) return;
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;

    const q = query(collection(db, 'departmentTeams'), where('ownerId', '==', ownerIdToUse));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const teamsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        teamsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDepartmentTeams(teamsData);
        setDepartmentTeamsLoading(false);
      },
      (error) => {
        logger.error('Error loading department teams:', error);
        setDepartmentTeamsLoading(false);
      }
    );

    return unsubscribe;
  }, [firebaseUser, teamOwnerId]);

  const addDepartmentTeam = async (teamData) => {
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;
    try {
      const docRef = await addDoc(collection(db, 'departmentTeams'), {
        ...teamData,
        ownerId: ownerIdToUse,
        members: teamData.members || [],
        createdAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      logger.error('Error adding department team:', error);
      return { success: false, error: error.message };
    }
  };

  const updateDepartmentTeam = async (teamId, updates) => {
    try {
      await updateDoc(doc(db, 'departmentTeams', teamId), updates);
      return { success: true };
    } catch (error) {
      logger.error('Error updating department team:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteDepartmentTeam = async (teamId) => {
    try {
      await deleteDoc(doc(db, 'departmentTeams', teamId));
      return { success: true };
    } catch (error) {
      logger.error('Error deleting department team:', error);
      return { success: false, error: error.message };
    }
  };

  // ── Subscription / Billing ───────────────────────────────
  const subscriptionData = useSubscription(firebaseUser, teamOwnerId);

  // ── Context value ─────────────────────────────────────────
  const value = useMemo(
    () => ({
      firebaseUser,
      currentUser,
      setCurrentUser,
      userProfile,
      ownerProfile,
      updateUserProfile,
      handleLogout,
      teamOwnerId,
      setTeamOwnerId,
      teamInfo,
      setTeamInfo,
      teamMembers,
      setTeamMembers,
      currentUserRole,
      departmentTeams,
      departmentTeamsLoading,
      addDepartmentTeam,
      updateDepartmentTeam,
      deleteDepartmentTeam,
      ...subscriptionData,
      // Functions (handleLogout, updateUserProfile, etc.) are stable useCallback refs — intentionally omitted from deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [
      firebaseUser,
      currentUser,
      userProfile,
      ownerProfile,
      teamOwnerId,
      teamInfo,
      teamMembers,
      currentUserRole,
      departmentTeams,
      departmentTeamsLoading,
      subscriptionData,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
